 
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { dashboardEventsStreamUrl } from "../lib/api.js";
import { TENANT_EXTERNAL_ID_CHANGED } from "../lib/tenant-settings.js";

export type DashboardSseHints = {
  revocationHint: string | null;
  slaBreachHint: string | null;
  rateQuotaHint: string | null;
  lifecycleHint: string | null;
};

type DashboardSseContextValue = DashboardSseHints & {
  registerOnRevocation: (fn: () => void) => () => void;
  registerOnSlaTick: (fn: () => void) => () => void;
  registerOnIdentitiesBulk: (fn: () => void) => () => void;
  registerOnRateQuota: (fn: () => void) => () => void;
  registerOnLifecycle: (fn: () => void) => () => void;
};

export const DashboardSseContext = createContext<DashboardSseContextValue | null>(null);

/**
 * Single `EventSource` for tenant dashboard SSE (revocation + SLA ticks).
 * Mount once under the tenant app shell; consumers use `useDashboardEventStream`.
 */
export function DashboardSseProvider({ children }: { children: ReactNode }) {
  const [revocationHint, setRevocationHint] = useState<string | null>(null);
  const [slaBreachHint, setSlaBreachHint] = useState<string | null>(null);
  const [rateQuotaHint, setRateQuotaHint] = useState<string | null>(null);
  const [lifecycleHint, setLifecycleHint] = useState<string | null>(null);
  const revocationRefreshers = useRef(new Set<() => void>());
  const slaRefreshers = useRef(new Set<() => void>());
  const identitiesBulkRefreshers = useRef(new Set<() => void>());
  const rateQuotaRefreshers = useRef(new Set<() => void>());
  const lifecycleRefreshers = useRef(new Set<() => void>());

  const registerOnRevocation = useCallback((fn: () => void) => {
    revocationRefreshers.current.add(fn);
    return () => {
      revocationRefreshers.current.delete(fn);
    };
  }, []);

  const registerOnSlaTick = useCallback((fn: () => void) => {
    slaRefreshers.current.add(fn);
    return () => {
      slaRefreshers.current.delete(fn);
    };
  }, []);

  const registerOnIdentitiesBulk = useCallback((fn: () => void) => {
    identitiesBulkRefreshers.current.add(fn);
    return () => {
      identitiesBulkRefreshers.current.delete(fn);
    };
  }, []);

  const registerOnRateQuota = useCallback((fn: () => void) => {
    rateQuotaRefreshers.current.add(fn);
    return () => {
      rateQuotaRefreshers.current.delete(fn);
    };
  }, []);

  const registerOnLifecycle = useCallback((fn: () => void) => {
    lifecycleRefreshers.current.add(fn);
    return () => {
      lifecycleRefreshers.current.delete(fn);
    };
  }, []);

  const notifyRevocation = useCallback(() => {
    for (const fn of revocationRefreshers.current) {
      try {
        fn();
      } catch {
        /* ignore */
      }
    }
  }, []);

  const notifySla = useCallback(() => {
    for (const fn of slaRefreshers.current) {
      try {
        fn();
      } catch {
        /* ignore */
      }
    }
  }, []);

  const notifyIdentitiesBulk = useCallback(() => {
    for (const fn of identitiesBulkRefreshers.current) {
      try {
        fn();
      } catch {
        /* ignore */
      }
    }
  }, []);

  const notifyRateQuota = useCallback(() => {
    for (const fn of rateQuotaRefreshers.current) {
      try {
        fn();
      } catch {
        /* ignore */
      }
    }
  }, []);

  const notifyLifecycle = useCallback(() => {
    for (const fn of lifecycleRefreshers.current) {
      try {
        fn();
      } catch {
        /* ignore */
      }
    }
  }, []);

  useEffect(() => {
    let es: EventSource | null = null;

    const connect = () => {
      const url = dashboardEventsStreamUrl();
      es?.close();
      es = null;
      if (!url) {
        setRevocationHint(null);
        setSlaBreachHint(null);
        setRateQuotaHint(null);
        setLifecycleHint(null);
        return;
      }

      es = new EventSource(url);

      es.addEventListener("revocation", (ev) => {
        const msg = (ev as MessageEvent).data as string;
        try {
          const o = JSON.parse(msg) as { kind?: string; sessionId?: string };
          const kind = o.kind ?? "revocation";
          const sid =
            typeof o.sessionId === "string" ? o.sessionId.slice(0, 12) : "session";
          setRevocationHint(`${kind.replace(/_/g, " ")} · ${sid}…`);
        } catch {
          setRevocationHint("Revocation event");
        }
        window.setTimeout(() => setRevocationHint(null), 12_000);
        notifyRevocation();
      });

      es.addEventListener("sla_tick", (ev) => {
        const msg = (ev as MessageEvent).data as string;
        try {
          const o = JSON.parse(msg) as { breaches?: Array<{ metric: string }> };
          const n = o.breaches?.length ?? 0;
          if (n > 0) {
            setSlaBreachHint(`SLA: ${n} threshold breach(es) in rolling window`);
          } else {
            setSlaBreachHint(null);
          }
        } catch {
          setSlaBreachHint(null);
        }
        notifySla();
      });

      es.addEventListener("identities_bulk", () => {
        notifyIdentitiesBulk();
      });

      es.addEventListener("lifecycle", (ev) => {
        const msg = (ev as MessageEvent).data as string;
        let kind = "lifecycle";
        try {
          const o = JSON.parse(msg) as { kind?: string };
          kind = o.kind ?? "lifecycle";
          setLifecycleHint(`${kind.replace(/\./g, " ")} · cascaded to sessions`);
        } catch {
          setLifecycleHint("Lifecycle event");
        }
        window.setTimeout(() => setLifecycleHint(null), 12_000);
        if (kind.startsWith("elevation.")) {
          notifyLifecycle();
        }
      });

      es.addEventListener("rate_quota", (ev) => {
        const msg = (ev as MessageEvent).data as string;
        try {
          const o = JSON.parse(msg) as {
            count?: number;
            limit?: number;
            ratio?: number;
            key?: string;
          };
          const pct = typeof o.ratio === "number" ? Math.round(o.ratio * 100) : null;
          const c = o.count ?? "?";
          const lim = o.limit ?? "?";
          setRateQuotaHint(
            pct != null
              ? `Rate limit: ${pct}% of tier (${c}/${lim}) on ${String(o.key ?? "bucket").slice(0, 48)}…`
              : "Approaching API rate limit for this tenant."
          );
        } catch {
          setRateQuotaHint("Approaching API rate limit for this tenant.");
        }
        window.setTimeout(() => setRateQuotaHint(null), 20_000);
        notifyRateQuota();
      });

      es.onerror = () => {
        /* Browser reconnects using `retry:` from the server; do not call close() here. */
      };
    };

    connect();
    const onTenantChanged = () => connect();
    /** Close before unload so browsers don't log "connection interrupted while loading". */
    const onPageHide = () => {
      es?.close();
      es = null;
    };
    window.addEventListener(TENANT_EXTERNAL_ID_CHANGED, onTenantChanged);
    window.addEventListener("pagehide", onPageHide);
    return () => {
      window.removeEventListener(TENANT_EXTERNAL_ID_CHANGED, onTenantChanged);
      window.removeEventListener("pagehide", onPageHide);
      es?.close();
    };
  }, [notifyRevocation, notifySla, notifyIdentitiesBulk, notifyRateQuota, notifyLifecycle]);

  const value = useMemo(
    () => ({
      revocationHint,
      slaBreachHint,
      rateQuotaHint,
      lifecycleHint,
      registerOnRevocation,
      registerOnSlaTick,
      registerOnIdentitiesBulk,
      registerOnRateQuota,
      registerOnLifecycle,
    }),
    [
      revocationHint,
      slaBreachHint,
      rateQuotaHint,
      lifecycleHint,
      registerOnRevocation,
      registerOnSlaTick,
      registerOnIdentitiesBulk,
      registerOnRateQuota,
      registerOnLifecycle,
    ]
  );

  return (
    <DashboardSseContext.Provider value={value}>{children}</DashboardSseContext.Provider>
  );
}

export function useDashboardSseContext(): DashboardSseContextValue | null {
  return useContext(DashboardSseContext);
}
